import { Column, ColumnKeys } from './grid-config';
import {
  GetRowsRequest,
  GetSummaryRequest,
  GetSummaryResponse,
  SetFiltersRequest,
} from '../../../../../../../../grid/types/common';
import { GridBaseDataProvider } from '../../../../../../../../grid/GridBaseDataProvider';
import { UserGroup, UserGroupsResponse } from '@nirvana/shared/dtos/types/policies/common';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from '../../../../../../../../grid/gridView/types';
import {
  PolicyDependenciesContainer,
  UserGroupsApiClients,
} from '../../../../../../../common/policyPanel/uiSettings/dependencies/PolicyDependenciesContainer';
import { ExchangeUserGroupStatus } from '../../../../../utils/uiSettings/exchange/exchange-protection.types';
import { Filter } from '../../../../../../../../grid/gridFilters/types';
import { convertGridFiltersToExtJsFilters } from '../../../../../../../../grid/utils/filters';

export interface UserGroupUiData {
  _id: string;
  name: string;
  specifics: {
    guid: string;
  };
  status: ExchangeUserGroupStatus;
}

export class GzExchangeUserGroupsGridDataProvider extends GridBaseDataProvider<Column, ColumnKeys> {
  // private companyFilterDataProvider = API_TYPE_POLICY_EXCHANGE;
  private cachedGetUserGroupsResponse?: UserGroupsResponse;
  private clients: UserGroupsApiClients;
  private selectedItems: string[] = [];

  // caching is used when enabling/disabling user groups, because we don't want to make a request to the backend in this case
  private useCache = false;

  set selected(selectedItems: string[]) {
    this.selectedItems = selectedItems;
  }

  get selected(): string[] {
    return this.selectedItems;
  }

  public addToSelected(guid: string) {
    this.useCache = true;
    this.selectedItems.push(guid);
  }

  public removeFromSelected(guid: string) {
    this.useCache = true;
    this.selectedItems = this.selectedItems.filter(item => item !== guid);
  }

  constructor() {
    super();
    this.clients = PolicyDependenciesContainer.getInstance().resolveDependency('UserGroups') as UserGroupsApiClients;
  }

  async getSummary(request: GetSummaryRequest<ColumnKeys>): Promise<GetSummaryResponse> {
    const pageNumber = request.pageNumber ?? DEFAULT_PAGE_NUMBER;
    const pageSize = request.pageSize ?? DEFAULT_PAGE_SIZE;
    const sortColumnName = request.sortColumnName ?? null;
    const sortDirection = request.sortDirection ?? null;
    const convertedFiltersToGzBackend = this.getConvertedFrontendFiltersToGzBackendFilters(request.filters);

    // caching is used when enabling/disabling user groups
    if (this.useCache) {
      this.useCache = false;
      return {
        summary: '',
        numItems: this.cachedGetUserGroupsResponse?.total ?? 0,
      };
    }

    try {
      this.cachedGetUserGroupsResponse = await this.clients.ReadRecords({
        page: pageNumber,
        limit: pageSize,
        sort: sortColumnName,
        dir: sortDirection,
        filter: convertGridFiltersToExtJsFilters(convertedFiltersToGzBackend),
      });
    } catch (error) {
      console.error(error);
      this.cachedGetUserGroupsResponse = { success: false, data: [], total: 0 };
    }

    return {
      summary: '',
      numItems: this.cachedGetUserGroupsResponse.total,
    };
  }

  async getRows(request: GetRowsRequest<Column, ColumnKeys>) {
    if (!this.cachedGetUserGroupsResponse) {
      console.error('no cached user groups response');
    }

    const convertedItems = this.convertUserGroupsToGridFormat(this.cachedGetUserGroupsResponse?.data ?? []);

    return {
      success: true,
      rawEvents: convertedItems,
    };
  }

  private convertUserGroupsToGridFormat(items: UserGroup[]): UserGroupUiData[] {
    return items.map(item => {
      return {
        _id: item._id,
        name: item.name,
        specifics: {
          guid: item.specifics.guid,
        },
        status: this.selectedItems.includes(item.specifics.guid)
          ? ExchangeUserGroupStatus.ENABLED
          : ExchangeUserGroupStatus.DISABLED,
      };
    });
  }

  private getConvertedFrontendFiltersToGzBackendFilters(
    filters: Filter<ColumnKeys | 'specifics.guid'>[]
  ): Filter<ColumnKeys | 'specifics.guid'>[] {
    // search for status filter
    const statusFilterIdx = filters.findIndex(filter => Object.keys(filter)[0] === ColumnKeys.STATUS);
    if (statusFilterIdx === -1) {
      return filters;
    }

    // if status filter is not selected, remove it (selection must be singluar)
    if (filters[statusFilterIdx][ColumnKeys.STATUS].in.length !== 1) {
      delete filters[statusFilterIdx];
      return filters;
    }

    // get filtered status value
    const status = filters[statusFilterIdx][ColumnKeys.STATUS].in[0];

    // set status filter to enabled and add selected guids
    if (status === ExchangeUserGroupStatus.ENABLED) {
      filters[statusFilterIdx][ColumnKeys.STATUS] = { value: ExchangeUserGroupStatus.ENABLED };
      filters.push({ 'specifics.guid': { value: this.selectedItems } });
    }

    // set status filter to disabled and add selected guids
    if (status === ExchangeUserGroupStatus.DISABLED) {
      filters[statusFilterIdx][ColumnKeys.STATUS] = { value: ExchangeUserGroupStatus.DISABLED };
      filters.push({ 'specifics.guid': { value: this.selectedItems } });
    }

    return filters;
  }

  async setFilters(request: SetFiltersRequest<ColumnKeys>): Promise<{} | undefined> {
    return {};
  }
}
